import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { CHILD_REGISTRATION_LIST, CHILD_REGISTRATION_STORE, CHILD_REGISTRATION_DELETE, CHILD_REGISTRATION_VIEW } from '../../api/constants';

// The registration-package API wraps the actual record inside
// { user_details, registration_package }, sometimes nested one level deeper
// under `data` too. Unwrap it so callers always get flat registration fields
// (id, child_first_name, ...) instead of the wrapper object.
const extractRegistrationPackage = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;

  const level1 = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  const registrationPackage = level1.registration_package || raw.registration_package;

  if (registrationPackage) {
    const userDetails = level1.user_details || raw.user_details || {};
    return {
      ...registrationPackage,
      child_first_name: userDetails.first_name || registrationPackage.child_first_name || '',
      child_last_name: userDetails.last_name || registrationPackage.child_last_name || '',
      child_dob: userDetails.user_setting?.dob || registrationPackage.child_dob || '',
      user_details: userDetails
    };
  }

  return raw;
};

// Fetch Child Registrations
export const fetchChildRegistrations = createAsyncThunk(
  'childRegistration/fetchRegistrations',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      
      const response = await apiHandler(CHILD_REGISTRATION_LIST, 'POST');
      return response.data.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch child registrations');
    }
  }
);

// Submit Child Registration (Create/Edit)
export const submitChildRegistration = createAsyncThunk(
  'childRegistration/submitRegistration',
  async (registrationData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(registrationData).forEach(key => {
        if (registrationData[key] !== null && registrationData[key] !== undefined && registrationData[key] !== '') {
          formData.append(key, registrationData[key]);
        }
      });
      
      const response = await apiHandler(CHILD_REGISTRATION_STORE, 'POST', formData);

      // apiHandler() never throws - its POST case catches the axios error
      // itself and *resolves* with that error object instead of rejecting
      // (see api/apihandler.js), so a failed request never reaches this
      // function's own catch block below; the 422/401/... handling there
      // was dead code for this call. Detect and unwrap that shape here,
      // same as apihandler.js's own getApiErrorMessage() has to.
      if (response?.isAxiosError || (response?.response && response?.data === undefined)) {
        const errRes = response.response;
        const statusCode = errRes?.status;

        if (statusCode === 422 && errRes?.data?.errors) {
          return rejectWithValue({
            errors: errRes.data.errors,
            message: 'Validation failed',
            statusCode: 422
          });
        }
        if (statusCode === 401) {
          return rejectWithValue({ message: 'Your session has expired. Please login again.', statusCode: 401 });
        }
        if (statusCode === 403) {
          return rejectWithValue({ message: 'You do not have permission to perform this action.', statusCode: 403 });
        }
        if (statusCode === 404) {
          return rejectWithValue({ message: 'The requested resource was not found.', statusCode: 404 });
        }
        if (statusCode === 500) {
          return rejectWithValue({ message: 'Server error occurred. Please try again later.', statusCode: 500 });
        }
        return rejectWithValue({
          message: errRes?.data?.message || response?.message || 'Failed to submit child registration',
          statusCode: statusCode || 0
        });
      }

      // Check for successful status codes (200, 201)
      const statusCode = response?.status || response?.data?.status;
      if (statusCode && (statusCode === 200 || statusCode === 201)) {
        // Return the response data - it should contain the created/updated registration
        const rawData = response.data?.data || response.data || {};
        return {
          success: true,
          data: extractRegistrationPackage(rawData),
          statusCode: statusCode
        };
      }

      // If status code is not 200/201, treat as error
      return rejectWithValue({
        message: response.data?.message || 'Unexpected response from server',
        statusCode: statusCode
      });

    } catch (error) {
      // Defensive fallback only - apiHandler() doesn't normally throw (see
      // above), but keep this in case something upstream of it does (e.g. a
      // network failure before axios even runs).
      // Handle different HTTP status codes
      const statusCode = error.response?.status;
      
      // 422 - Validation errors
      if (statusCode === 422 && error.response?.data?.errors) {
        return rejectWithValue({
          errors: error.response.data.errors,
          message: 'Validation failed',
          statusCode: 422
        });
      }
      
      // 401 - Unauthorized
      if (statusCode === 401) {
        return rejectWithValue({
          message: 'Your session has expired. Please login again.',
          statusCode: 401
        });
      }
      
      // 403 - Forbidden
      if (statusCode === 403) {
        return rejectWithValue({
          message: 'You do not have permission to perform this action.',
          statusCode: 403
        });
      }
      
      // 404 - Not Found
      if (statusCode === 404) {
        return rejectWithValue({
          message: 'The requested resource was not found.',
          statusCode: 404
        });
      }
      
      // 500 - Server Error
      if (statusCode === 500) {
        return rejectWithValue({
          message: 'Server error occurred. Please try again later.',
          statusCode: 500
        });
      }
      
      // Generic error handling
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to submit child registration',
        statusCode: statusCode || 0
      });
    }
  }
);

// Fetch Registration Packages (using CHILD_REGISTRATION_VIEW API)
export const fetchRegistrationPackages = createAsyncThunk(
  'childRegistration/fetchPackages',
  async ({ page = 1, limit = 10, student_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      if (student_id) {
        formData.append('student_id', student_id);
      }

      const response = await apiHandler(CHILD_REGISTRATION_VIEW, 'POST', formData);
      
      // Handle different response structures
      // API returns: response.data.data.data.{user_details, registration_package}
      const responseData = response.data.data;
      
      // If data is an array, return it directly
      if (Array.isArray(responseData)) {
        return responseData;
      }
      
      // Check if nested data exists (response.data.data.data)
      if (responseData?.data) {
        const nestedData = responseData.data;
        
        // If nested data has registration_package
        if (nestedData.registration_package) {
          const userDetails = nestedData.user_details || {};
          const registrationPackage = nestedData.registration_package;
          
          // Merge user_details with registration_package and map child name from user
          return [{
            ...registrationPackage,
            child_first_name: userDetails.first_name || '',
            child_last_name: userDetails.last_name || '',
            child_dob: userDetails.user_setting?.dob || '',
            child_gender: userDetails.user_setting?.gender === 1 ? 'Male' : userDetails.user_setting?.gender === 2 ? 'Female' : 'Other',
            user_details: userDetails
          }];
        }
      }
      
      // If data has registration_package directly, extract and return as array
      if (responseData?.registration_package) {
        const userDetails = responseData.user_details || {};
        const registrationPackage = responseData.registration_package;
        
        // Merge user_details with registration_package for display
        return [{
          ...registrationPackage,
          child_first_name: userDetails.first_name || '',
          child_last_name: userDetails.last_name || '',
          child_dob: userDetails.user_setting?.dob || '',
          child_gender: userDetails.user_setting?.gender === 1 ? 'Male' : userDetails.user_setting?.gender === 2 ? 'Female' : 'Other',
          user_details: userDetails
        }];
      }
      
      // If data.data exists and is an array, return it
      if (Array.isArray(responseData?.data)) {
        return responseData.data;
      }
      
      // Default to empty array
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch registration packages');
    }
  }
);

// Delete Child Registration
export const deleteChildRegistration = createAsyncThunk(
  'childRegistration/deleteRegistration',
  async (registrationId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('id', registrationId);
      
      const response = await apiHandler(CHILD_REGISTRATION_DELETE, 'POST', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete child registration');
    }
  }
);

const childRegistrationSlice = createSlice({
  name: 'childRegistration',
  initialState: {
    registrations: [],
    loading: false,
    // True once a fetch of this student's registrations has completed at
    // least once (success or failure) — distinct from `loading`, which is
    // false both before the very first fetch starts AND after it finishes.
    // Callers that decide "is the registration packet incomplete?" from
    // `registrations` (the header banner, the profile tab's red dot) need
    // this to tell "genuinely empty" apart from "haven't asked the server
    // yet", otherwise the empty initial state reads as "incomplete" and
    // flashes that warning on every load before flipping to correct once
    // the real data arrives.
    initialized: false,
    submitLoading: false,
    deleteLoading: false,
    error: null,
    totalPages: 1,
    currentPage: 1
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.registrations = [];
      state.loading = false;
      state.initialized = false;
      state.submitLoading = false;
      state.deleteLoading = false;
      state.error = null;
      state.totalPages = 1;
      state.currentPage = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Child Registrations
      .addCase(fetchChildRegistrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChildRegistrations.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.registrations = action.payload;
        state.error = null;
      })
      .addCase(fetchChildRegistrations.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })

      // Fetch Registration Packages
      .addCase(fetchRegistrationPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRegistrationPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.registrations = action.payload;
        state.error = null;
      })
      .addCase(fetchRegistrationPackages.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })

      // Submit Child Registration
      .addCase(submitChildRegistration.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
      })
      .addCase(submitChildRegistration.fulfilled, (state, action) => {
        state.submitLoading = false;
        
        // Extract the actual data from the response structure
        const responseData = action.payload?.data || action.payload;
        
        // Ensure registrations is an array
        if (!Array.isArray(state.registrations)) {
          state.registrations = [];
        }
        
        // If it's an edit, update the existing record
        // Use == (not ===) since responseData.id may come back as a string while
        // registrations fetched via the list API have numeric ids - a strict
        // mismatch here causes the update to be missed and inserted as a duplicate.
        const existingIndex = state.registrations.findIndex(
          reg => reg.id == responseData.id
        );
        
        if (existingIndex !== -1) {
          state.registrations[existingIndex] = responseData;
        } else {
          // If it's new, add to the beginning
          state.registrations.unshift(responseData);
        }
        
        state.error = null;
      })
      .addCase(submitChildRegistration.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      })

      // Delete Child Registration
      .addCase(deleteChildRegistration.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteChildRegistration.fulfilled, (state, action) => {
        state.deleteLoading = false;
        
        // Ensure registrations is an array
        if (Array.isArray(state.registrations)) {
          state.registrations = state.registrations.filter(
            reg => reg.id !== action.meta.arg
          );
        }
        
        state.error = null;
      })
      .addCase(deleteChildRegistration.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  }
});

// Selectors
export const selectAllRegistrations = (state) => {
  const registrations = state.childRegistration.registrations;
  return Array.isArray(registrations) ? registrations : [];
};

export const selectRegistrationById = (state, registrationId) => {
  const registrations = state.childRegistration.registrations;
  return Array.isArray(registrations) 
    ? registrations.find(reg => reg.id == registrationId) // Use == to handle string/number comparison
    : undefined;
};

export const selectLoading = (state) => state.childRegistration.loading;
export const selectInitialized = (state) => state.childRegistration.initialized;
export const selectSubmitLoading = (state) => state.childRegistration.submitLoading;
export const selectDeleteLoading = (state) => state.childRegistration.deleteLoading;
export const selectError = (state) => state.childRegistration.error;

export const { clearError, resetState } = childRegistrationSlice.actions;

export default childRegistrationSlice.reducer;
